<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('employees_who_ordered')"
    :subtitle="dialogSubtitle"
    color="primary"
    hide-footer
  >
    <v-card-text>
      <!-- Loading State -->
      <empty-state
        v-if="loading"
        :title="$t('loading')"
        :loading="true"
      />

      <!-- Empty State -->
      <empty-state
        v-else-if="employeesList.length === 0"
        icon="mdi-account-off"
        :title="$t('no_employees_found')"
        :subtitle="$t('no_employees_ordered_this_meal')"
      />

      <!-- Employees Table -->
      <v-data-table
        v-else
        :headers="headers"
        :items="employeesList"
        class="striped-table ordera-box-shadow"
        :loading="loading"
        hide-default-footer
        disable-pagination
        mobile-breakpoint="0"
      >
        <template v-slot:item.num="{ index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.user="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar size="36" color="primary" class="mr-3">
              <span class="white--text text-subtitle-2">
                {{ getInitials(item.name, item.lastname) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">
                {{ toLatin(item.name) }} {{ toLatin(item.lastname) }}
              </div>
              <div class="text-caption grey--text">
                {{ item.email || "-" }}
              </div>
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </fullscreen-dialog>
</template>

<script>
import reportService from "@/services/report";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import hasModel from "@/mixins/hasModel";

export default {
  name: "EmployeesWhoOrderedDialog",
  mixins: [hasModel],
  components: {
    FullscreenDialog,
    EmptyState,
  },
  props: {
    selectedMeal: {
      type: Object,
      default: null,
    },
    selectedCompany: {
      type: Object,
      default: null,
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    companyUserData: null,
  }),
  computed: {
    dialogSubtitle() {
      const mealName = this.selectedMeal ? this.toLatin(this.selectedMeal.name) : "";
      const dateRange = this.formattedDateRange;
      if (mealName && dateRange) {
        return `${mealName} | ${dateRange}`;
      }
      return mealName || dateRange || "";
    },
    formattedDateRange() {
      if (!this.filters || !this.filters.dates) return "";
      const from = this.filters.dates.from;
      const to = this.filters.dates.to;
      const formattedFrom = this.localeDate(from);
      const formattedTo = this.localeDate(to);
      if (from === to) {
        return formattedFrom;
      }
      return `${formattedFrom} - ${formattedTo}`;
    },
    employeesList() {
      if (!this.companyUserData || !this.selectedMeal) {
        return [];
      }

      // Find the meal in the response data that matches the selected meal id
      const meals = this.companyUserData.meals || [];
      const matchingMeal = meals.find((meal) => meal.id === this.selectedMeal.id);

      if (!matchingMeal || !matchingMeal.users) {
        return [];
      }

      // Create a map to collect users with their shift quantities
      const usersMap = {};
      const users = matchingMeal.users;

      // Process each shift
      ["1-smena", "2-smena", "3-smena", "4-smena"].forEach((shiftKey, index) => {
        if (users[shiftKey]) {
          users[shiftKey].forEach((user) => {
            if (!usersMap[user.id]) {
              usersMap[user.id] = {
                ...user,
                shift_one: 0,
                shift_two: 0,
                shift_three: 0,
                shift_four: 0,
              };
            }
            // Increment the count for this shift
            const shiftField = ["shift_one", "shift_two", "shift_three", "shift_four"][
              index
            ];
            usersMap[user.id][shiftField] += 1;
          });
        }
      });

      return Object.values(usersMap);
    },
    headers() {
      return [
        { text: this.$t("id"), value: "id", width: "80px" },
        { text: this.$t("external_code"), value: "external_code", width: "100px" },
        { text: this.$t("fullname"), value: "user" },
        { text: this.$t("1st_shift"), value: "shift_one", align: "center" },
        { text: this.$t("2nd_shift"), value: "shift_two", align: "center" },
        { text: this.$t("3rd_shift"), value: "shift_three", align: "center" },
        { text: this.$t("4th_shift"), value: "shift_four", align: "center" },
      ];
    },
  },
  watch: {
    model: {
      handler(newVal) {
        if (newVal && this.selectedCompany) {
          this.fetchCompanyUsers();
        }
      },
      immediate: true,
    },
  },
  methods: {
    getInitials(name, lastname) {
      const first = name ? name.charAt(0).toUpperCase() : "";
      const last = lastname ? lastname.charAt(0).toUpperCase() : "";
      return first + last;
    },
    async fetchCompanyUsers() {
      if (!this.selectedCompany || !this.selectedCompany.id) {
        console.warn("No company selected");
        return;
      }

      this.loading = true;

      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };

      try {
        const response = await reportService.getCompanyUserList(
          this.selectedCompany.id,
          params
        );
        this.companyUserData = response.data;
      } catch (error) {
        console.error("Error fetching company users:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.striped-table {
  margin-top: 16px;
}
</style>
